import { createContext, useContext, useEffect, useState } from "react";
import api, { apiError, setEnrollToken, setVistaHeader } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=auth
  const [booting, setBooting] = useState(true);
  const [vista, setVistaState] = useState(() => sessionStorage.getItem("vista_socio") || "mia");
  const setVista = (v) => { sessionStorage.setItem("vista_socio", v); setVistaState(v); };
  const resetVista = () => setVista("mia");
  const soloTeam = vista === "team";

  useEffect(() => {
    const PUBLIC = ["/login", "/forgot-password", "/reset-password", "/cliente/", "/condivisione/"];
    if (PUBLIC.some((p) => window.location.pathname.startsWith(p))) {
      setUser(false); setBooting(false); return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setBooting(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return data; // caller handles the 2FA code step
    resetVista();
    if (data.mfa_enrollment_required) {
      // SEC: no session issued yet; use the scoped enrollment token for 2FA setup/activate only.
      setEnrollToken(data.enroll_token || null);
      setUser({ mfa_enrollment_required: true, totp_enabled: false });
      return data;
    }
    setUser({ ...data.user, mfa_enrollment_required: false });
    return data;
  };

  const verify2fa = async (mfa_token, code, remember_device = true) => {
    const { data } = await api.post("/auth/2fa/verify", { mfa_token, code, remember_device });
    resetVista();
    setUser({ ...data.user, mfa_enrollment_required: false });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
    window.location.href = "/login";
  };

  const patchUser = (fields) => setUser((u) => (u ? { ...u, ...fields } : u));

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const switchView = async (view) => {
    await api.post("/auth/active-view", { view });
    await refreshUser();
  };

  const changePassword = async (new_password, current_password) => {
    await api.post("/auth/change-password", { new_password, current_password });
    setUser((u) => ({ ...u, must_change_password: false }));
  };

  const isDirezione = user && user.role === "direzione";
  const isDirettore = user && user.role === "direttore_commerciale";
  const isAgente = user && (user.role === "agente" || user.role === "buyer");
  const isSegreteria = !!(user && (user.role === "segreteria" || user.is_segreteria));
  const isFotografo = !!(user && user.role === "fotografo");
  const canFinance = !!(user && (user.role === "direzione" || isSegreteria));
  const canManageUsers = user && (user.role === "direzione" || user.role === "direttore_commerciale" || isSegreteria);
  const vistaMia = !!(isDirezione && vista === "mia");
  const monitoraggio = !!(isDirezione && vista !== "mia");
  const isSocio = !!(user && (user.roles || []).includes("direzione"));
  const haTeam = !!(isDirezione && user.team_id && (user.roles || []).includes("direttore_commerciale"));
  setVistaHeader(vistaMia ? "mia" : monitoraggio && soloTeam && haTeam ? "team" : "");

  return (
    <AuthContext.Provider value={{ user, booting, login, verify2fa, logout, changePassword, patchUser, refreshUser, switchView, vista, setVista, soloTeam, isSocio, vistaMia, monitoraggio, haTeam, isDirezione, isDirettore, isAgente, isSegreteria, isFotografo, canFinance, canManageUsers }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
